import type { App, Route } from "@pimo/pimo-app-builder";
import { ResourceLockPlugin } from "@pimo/resource-lock-plugin";
import { BffOe, BffOeInitiatives, Initiative } from "aint-types";
import { convertImpactValuesUnit, LOCK_RESOURCE_NAMES } from "aint-utils";
import { generatePath } from "react-router-dom";

import { InAppState } from "../app/app";
import { APP_ROUTES } from "../app/constants";
import {
  fetchEditInitiative,
  fetchOE,
  fetchOEInitiatives,
  saveInitiative,
} from "../app/helpers/fetch-helpers";
import { UpdateImpactDialog } from "../app/pages/update-impact-dialog/update-impact-dialog";
import { roundFteImpact } from "../app/utils";
import { INFormConfirmationDialog } from "../components/in-form-confirmation-dialog";

export function buildReportingFormRoute(
  app: App<InAppState>,
  parentRoute: Route<"oeId" | "viewname", InAppState>
) {
  let oeId: string, initiativeUid: string;

  const view = app.createView({
    name: "Update-Impact",
  });

  const editDialog = view.addComponent({
    component: UpdateImpactDialog,
  });

  editDialog.mapState(
    ({
      currentOE,
      currentOEInitiatives,
      currentEditInitiative,
      editFormLoading: isLoading,
      editFormSaving: isSaving,
      initiativesLockDetails,
      userProfile,
    }) => {
      return {
        title: `Impact Reporting - ${currentOE?.operatingEntity ?? ""}`,
        initiatives:
          currentOEInitiatives.initiatives?.filter(
            (initiative) =>
              !initiative.draft && initiative.initiativePhase !== "L0 - Idea"
          ) ?? [],
        activeInitiative: convertImpactValuesUnit({
          unit: "k EUR",
          initiative: currentEditInitiative,
          plannedOrActualArray: ["Planned", "Actual"],
        }),
        dialogType: "update-impact",
        initiativesLockDetails,
        isLoading,
        isSaving,
        currentUser: {
          name: userProfile?.name ?? "",
          email: userProfile?.email ?? "",
        },
        currentProgres:
          currentOEInitiatives.updateStatusOverview?.updateProgress ?? 0,
        switchReportingLock,
      };
    }
  );

  function switchReportingLock(
    oldInitiativeId: number,
    newInitiativeId: number
  ) {
    const resourceLockPlugin =
      app.getPluginByName<ResourceLockPlugin<InAppState>>("ResourceLockPlugin");
    resourceLockPlugin?.unlock(
      `${LOCK_RESOURCE_NAMES.initiative}-${oldInitiativeId}`
    );
    resourceLockPlugin?.lock(
      `${LOCK_RESOURCE_NAMES.initiative}-${newInitiativeId}`
    );
  }

  const route = parentRoute.createChildRoute({
    path: APP_ROUTES.impactUpdate,
    view: view,
  }) as Route<"oeId" | "viewname" | "initiativeUid">;

  route.on("load", async ({ payload }) => {
    if (!payload?.parameters?.oeId || !payload?.parameters.initiativeUid) {
      return;
    }

    app.patchAppState({ editFormLoading: true });

    initiativeUid = payload.parameters.initiativeUid;
    oeId = payload.parameters.oeId;

    const statePatch: {
      currentOE?: BffOe;
      currentEditInitiative?: Initiative;
      currentOEInitiatives?: BffOeInitiatives;
    } = {};

    statePatch.currentEditInitiative = await fetchEditInitiative(
      oeId,
      initiativeUid
    );

    const appState = app.getAppState();

    if (!appState.currentOE) {
      statePatch.currentOE = await fetchOE(oeId);
    }

    if (appState.currentOEInitiatives.initiatives?.length === 0) {
      statePatch.currentOEInitiatives = await fetchOEInitiatives(oeId);
    }

    app.patchAppState({ ...statePatch, editFormLoading: false });
  });

  registerFormEvents();
  createConfirmationDialog();

  function registerFormEvents() {
    editDialog.on(
      "report-impact-dialog:submit",
      async ({ payload: initiativeToSave = {} as Initiative }) => {
        convertImpactValuesUnit({
          unit: "EUR",
          initiative: initiativeToSave,
          plannedOrActualArray: ["Planned", "Actual"],
        });
        roundFteImpact(initiativeToSave);

        app.patchAppState({
          editFormSaving: true,
        });

        const savedInitiative = await saveInitiative({
          id: initiativeToSave.id,
          initiative: initiativeToSave,
          isReportingUpdate: true,
        });

        const currentOEInitiatives = await fetchOEInitiatives(oeId);

        app.patchAppState({
          currentEditInitiative: savedInitiative,
          currentOEInitiatives,
          editFormSaving: false,
          snackbar: {
            message: "Impact Reporting saved successfully",
            severity: "success",
            open: true,
            type: "snackbar",
          },
        });
      }
    );

    editDialog.on("report-impact-dialog:close-with-changes", () => {
      app.patchAppState({
        showEditFormConfirmationDialog: true,
        editFormConfirmationDialogType: "cancel-with-changes",
      });
    });

    editDialog.on("report-impact-dialog:close", () => {
      const resourceLockPlugin =
        app.getPluginByName<ResourceLockPlugin<InAppState>>(
          "ResourceLockPlugin"
        );
      resourceLockPlugin?.unlock(
        `${LOCK_RESOURCE_NAMES.initiative}-${app.getAppState().currentEditInitiative?.id}`
      );

      return app.navigate(
        generatePath(APP_ROUTES.operatingEntities, {
          viewname: "initiatives",
          oeId,
        })
      );
    });
  }

  function createConfirmationDialog() {
    const confirmationDialog = view.addComponent({
      component: INFormConfirmationDialog,
    });

    confirmationDialog.mapVisibility(
      ({ showEditFormConfirmationDialog }) =>
        showEditFormConfirmationDialog && showEditFormConfirmationDialog != null
    );

    confirmationDialog.mapState(({ editFormConfirmationDialogType }) => ({
      type: editFormConfirmationDialogType,
    }));

    function closeConfirmationDialog() {
      app.patchAppState({
        showEditFormConfirmationDialog: false,
      });
    }

    confirmationDialog.on("confirm", () => {
      closeConfirmationDialog();

      const resourceLockPlugin =
        app.getPluginByName<ResourceLockPlugin<InAppState>>(
          "ResourceLockPlugin"
        );
      const { editFormConfirmationDialogType } = app.getAppState();

      switch (editFormConfirmationDialogType) {
        case "cancel-with-changes":
          resourceLockPlugin?.unlock(
            `${LOCK_RESOURCE_NAMES.initiative}-${app.getAppState().currentEditInitiative?.id}`
          );

          app.navigate(
            generatePath(APP_ROUTES.operatingEntities, {
              oeId,
              viewname: "initiatives",
            })
          );
          return;

        default:
          break;
      }
    });

    confirmationDialog.on("abort", () => {
      closeConfirmationDialog();
    });
  }
}
